import _ from 'lodash';
import React, {FC} from 'react';
import {createSelector} from 'reselect';
import styled from 'styled-components';

import {ContactsManagerSearchBarContainer} from '../../../../libs/contacts-manager/src/lib/ui/web/common/contactsManagerSearchBarContainer';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {MouseEventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {
  ElectronPoint,
  handleTitleBarDoubleClickAsync,
  hideElectronDragRegionsAsync,
} from '../../../services/electron/electronBridgeOutbound';
import {getHasPermission, getHasSharedPermission} from '../../../store/data/access/accessSelectors';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {getIsElectron} from '../../../store/electron/electronSelectors';
import {
  getHasNewContactsManagerV0,
  getShouldHideAuditTrail,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {AppContentTypesEnum} from '../../../store/panels/app/parameters/appParameters';
import {
  AppPanelId,
  makeContactsManagerPanelId,
  makeInboxesPanelId,
} from '../../../store/panels/panelStateBase';
import {InboxesSearchBarContainer} from '../../inboxes/search/searchBar/inboxesSearchBarContainer';
import {AppSwitcher, appSwitcherButtonWidth} from './appSwitcher';
import {MacOsTrafficLights} from './macOsTrafficLights';
import {SettingsSection} from './settingsSection';
import {
  AppDefinition,
  buildApps,
  maxTopBarSearchBarWidth,
  minTopBarSearchBarWidth,
  topBarHeight,
  topBarHorizontalPadding,
  topBarVerticalPadding,
} from './topBarHelpers';
import {WindowsTitleBarControls} from './windowsTitleBar/windowsTitleBarControls';
import {WindowsTitleBarMenuButton} from './windowsTitleBar/windowsTitleBarMenuButton';

/*
 * Constants.
 */

const macOsTrafficLightPosition: ElectronPoint = {
  // Offset by 1px to visually match.
  x: topBarHorizontalPadding - 1,
  // Determined experimentally. (This is probably weird because macOS's y-axis is flipped.)
  y: 18,
};

/*
 * Props.
 */

interface TopBarProps {
  panelId: AppPanelId;
  contentType: AppContentTypesEnum;
}

/*
 * Styles.
 */

const StyledTopBarWrapper = styled.div`
  -webkit-app-region: drag;

  display: grid;
  grid-template-columns: 1fr 8px minmax(${minTopBarSearchBarWidth}px, ${maxTopBarSearchBarWidth}px) 8px 1fr;
  grid-template-areas: 'left . search . right';

  align-items: center;

  height: ${topBarHeight}px;
  padding: ${topBarVerticalPadding}px ${topBarHorizontalPadding}px;

  background-color: ${(p) => p.theme.brand};
`;

const StyledTopBarLeftWrapper = styled.div`
  grid-area: left;

  display: grid;
  grid-template-columns: min-content minmax(${appSwitcherButtonWidth}px, auto);
  grid-template-areas: 'electron-left apps';
`;

const StyledMacOsTrafficLights = styled(MacOsTrafficLights)`
  grid-area: electron-left;
  margin-right: 32px;
`;

const StyledWindowsTitleBarMenu = styled(WindowsTitleBarMenuButton)`
  grid-area: electron-left;
`;

const StyledTopBarRightWrapper = styled.div`
  grid-area: right;

  display: grid;
  grid-template-columns: auto min-content;
  grid-template-areas: 'settings electron-right';
`;

const StyledWindowsControls = styled(WindowsTitleBarControls)`
  grid-area: electron-right;
  margin: ${-topBarVerticalPadding}px ${-topBarHorizontalPadding}px ${-topBarVerticalPadding}px
    ${topBarHorizontalPadding}px;
`;

/*
 * Component.
 */

export const TopBar: FC<TopBarProps> = ({panelId, contentType}) => {
  const isElectron = useAppSelector(getIsElectron);
  const shouldHideAuditTrail = useAppSelector(getShouldHideAuditTrail);
  const hasNewContactsManagerV0 = useAppSelector(getHasNewContactsManagerV0);

  const apps = buildApps({
    shouldDisplayAuditTrail: !shouldHideAuditTrail,
    shouldDisplayNewContacts: hasNewContactsManagerV0,
  });

  const hasSharedOrImplicitPermission = useAppSelector(getHasSharedOrImplicitPermission);
  const allowedApps = apps.filter((app) => !app.permission || hasSharedOrImplicitPermission(app.permission));

  // TODO: Simplify me when contacts manager is released
  const selectedApp = _(allowedApps)
    .compact()
    .find((app) => app.contentType === contentType);

  useInitialEffect(() => {
    if (!isElectron) {
      return;
    }

    // Tell Electron to hide its drag regions, since we render our own.
    hideElectronDragRegionsAsync();
  });

  const onDoubleClick: MouseEventHandler = (event) => {
    if (!isElectron || event.defaultPrevented) {
      return;
    }

    event.preventDefault();
    handleTitleBarDoubleClickAsync();
  };

  return (
    <StyledTopBarWrapper onDoubleClick={onDoubleClick}>
      <StyledTopBarLeftWrapper>
        <StyledMacOsTrafficLights position={macOsTrafficLightPosition} />
        <StyledWindowsTitleBarMenu />
        <AppSwitcher panelId={panelId} apps={allowedApps} selectedApp={selectedApp} />
      </StyledTopBarLeftWrapper>
      {maybeRenderSearchBar(panelId, contentType, selectedApp, hasNewContactsManagerV0)}
      <StyledTopBarRightWrapper>
        <SettingsSection
          panelId={panelId}
          isSettingsAppSelected={contentType === AppContentTypesEnum.SETTINGS}
        />
        <StyledWindowsControls />
      </StyledTopBarRightWrapper>
    </StyledTopBarWrapper>
  );
};

/*
 * Selectors.
 */

export const getHasSharedOrImplicitPermission = createSelector(
  [getHasPermission, getHasSharedPermission, getUserNamespace],
  (hasPermission, hasSharedPermission, userNamespace) => (permission: PermissionsEnum) =>
    hasSharedPermission(permission) || hasPermission(userNamespace, permission),
);

/*
 * Helpers.
 */

// This implementation is not ideal as we're tying P0 with apps.
// This should be revisited when we'll split bundles.
const maybeRenderSearchBar = (
  panelId: AppPanelId,
  contentType: AppContentTypesEnum,
  selectedApp?: AppDefinition,
  hasNewContactsManagerV0?: boolean,
) => {
  if (!selectedApp) {
    return null;
  }

  if (contentType === AppContentTypesEnum.CONTACTS && hasNewContactsManagerV0) {
    return <ContactsManagerSearchBarContainer panelId={makeContactsManagerPanelId(panelId)} />;
  }

  if (contentType === AppContentTypesEnum.INBOXES) {
    return <InboxesSearchBarContainer panelId={makeInboxesPanelId(panelId)} />;
  }

  return null;
};
